import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { Calendar, Music, Clock, DollarSign, Menu, X, ArrowLeft, Home } from "lucide-react";
import CalendarView from "../components/CalendarView";
import PDPAModal from "../components/PDPAModal";
import Footer from "../components/Footer";
import LanguageSelector from "../components/LanguageSelector";
import { ORG_PHONE_RAW } from "../constants";

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPDPA, setShowPDPA] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Listen for PDPA modal open event from footer
    const handleOpenPDPA = () => setShowPDPA(true);
    window.addEventListener('openPDPA', handleOpenPDPA);
    return () => window.removeEventListener('openPDPA', handleOpenPDPA);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <header className="border-b-4 border-black bg-primary">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Back to Home Button */}
              <button
                onClick={() => navigate('/')}
                className="p-2 bg-white/20 border-2 border-white text-white hover:bg-white hover:text-primary transition-colors mr-2"
                title="Back to Home"
              >
                <Home className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent border-2 border-black flex items-center justify-center flex-shrink-0">
                <Music className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={3} />
              </div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-syne font-bold text-white tracking-tight notranslate" translate="no">
                KARAOKE BOOKING
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSelector variant="dark" />
              {user && (
                <>
                  {/* KJ Console - Only for non-admin KJs */}
                  {!user.is_admin && (
                    <button
                      onClick={() => navigate('/kj-console')}
                      className="brutal-btn-outline bg-white text-black border-white hover:bg-black hover:text-white"
                      data-testid="kj-console-btn"
                    >
                      KJ CONSOLE
                    </button>
                  )}
                  {/* My Schedule - Only for non-admin KJs */}
                  {!user.is_admin && (
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="brutal-btn-outline bg-white text-black border-white hover:bg-black hover:text-white"
                      data-testid="dashboard-btn"
                    >
                      MY SCHEDULE
                    </button>
                  )}
                  {user.is_admin && (
                    <button
                      onClick={() => navigate('/admin')}
                      className="brutal-btn-secondary"
                      data-testid="admin-btn"
                    >
                      ADMIN
                    </button>
                  )}
                </>
              )}
            </div>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector variant="dark" />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 bg-white border-2 border-black"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t-2 border-white/30">
              <div className="flex flex-col gap-3">
                {user && (
                  <>
                    {/* KJ Console - Only for non-admin KJs */}
                    {!user.is_admin && (
                      <button
                        onClick={() => { navigate('/kj-console'); setMobileMenuOpen(false); }}
                        className="w-full py-3 px-4 text-left font-bold bg-white text-black border-2 border-black"
                        data-testid="mobile-kj-console-btn"
                      >
                        🎤 KJ CONSOLE
                      </button>
                    )}
                    {/* My Schedule - Only for non-admin KJs */}
                    {!user.is_admin && (
                      <button
                        onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                        className="w-full py-3 px-4 text-left font-bold bg-white text-black border-2 border-black"
                        data-testid="mobile-dashboard-btn"
                      >
                        📋 MY SCHEDULE
                      </button>
                    )}
                    {user.is_admin && (
                      <button
                        onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                        className="w-full py-3 px-4 text-left font-bold bg-accent text-black border-2 border-black"
                        data-testid="mobile-admin-btn"
                      >
                        ⚙️ ADMIN PANEL
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Important Notice Banner for Court RN Members */}
      <div className="bg-amber-100 border-b-4 border-amber-500">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">📢</span>
            <div>
              <p className="font-bold text-amber-900 text-sm sm:text-base">
                For Court RN Members / Community Partners:
              </p>
              <p className="text-amber-800 text-sm">
                Please approach our RN Centre, call us at{' '}
                <a href={`tel:${ORG_PHONE_RAW}`} className="font-bold underline hover:text-amber-950">
                  {ORG_PHONE_RAW}
                </a>
                {' '}or email us at{' '}
                <a href="mailto:admin@yckcourtrn.com" className="font-bold underline hover:text-amber-950">
                  admin@yckcourtrn.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-accent border-b-4 border-black py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="brutal-card bg-white">
              <Clock className="w-8 h-8 mb-3" strokeWidth={2.5} />
              <h3 className="text-lg font-bold mb-2">TWO DAILY SLOTS</h3>
              <p className="font-mono text-sm">
                <strong>Afternoon:</strong> 1PM - 5PM<br />
                <strong>Evening:</strong> 6PM - 10PM
              </p>
            </div>
            <div className="brutal-card bg-white">
              <DollarSign className="w-8 h-8 mb-3" strokeWidth={2.5} />
              <h3 className="text-lg font-bold mb-2">PRICING</h3>
              <p className="font-mono text-sm">
                <strong>$90:</strong> Weekdays (Mon-Fri)<br />
                <strong>$100:</strong> Weekends &amp; Public Holidays<br />
                <span className="text-xs text-gray-500">Prices may vary — check calendar for exact pricing</span>
              </p>
            </div>
            <div className="brutal-card bg-white">
              <Calendar className="w-8 h-8 mb-3" strokeWidth={2.5} />
              <h3 className="text-lg font-bold mb-2">EASY BOOKING</h3>
              <p className="font-mono text-sm">
                Select a slot, confirm details<br />
                <span className="text-primary font-bold">For any settlement, please contact:</span><br />
                <strong>{ORG_PHONE_RAW}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Contact Admin for payment enquiries or date changes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-4xl font-syne font-bold mb-8 text-center tracking-tight">
          AVAILABLE SLOTS
        </h2>
        <CalendarView user={user} />
      </div>

      {/* Footer */}
      <Footer />

      <PDPAModal isOpen={showPDPA} onClose={() => setShowPDPA(false)} />
    </div>
  );
};

export default HomePage;
